import { withAuthenticationRequired } from '@auth0/auth0-react'
import type { AdUnit } from '@pubstack/common/src/adunit'
import { PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import type { RefreshGroup } from '@pubstack/common/src/refresh'
import { canAccess, getScopeRole, hasFeature, scopeFeature, type UserRole } from '@pubstack/common/src/rolesAndFeatures'
import type { AdminScope, ScopeId } from '@pubstack/common/src/scope'
import type { Site } from '@pubstack/common/src/site'
import { useAtomValue } from 'jotai'
import { type ComponentType, type FunctionComponent, type ReactElement, type ReactNode, useCallback, useEffect, useRef } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { matchRoutes, type NavigateProps, Outlet, type RouteMatch, type RouteObject, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import ProtectedRoute, { type PubstackRouteMeta, upsellOffers } from '~/auth/ProtectedRoute'
import type User from '~/auth/User'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { GlobalErrorPage } from '~/components/GlobalErrorPage'
import Layout from '~/components/layout/Layout'
import { AdminScopeDetail } from '~/modules/admin/AdminScopeDetail'
import { rewriteLegacyPath } from '~/utils/legacy-routes'
import { nodeStaffStateOfScope, useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { useScope } from '~/hooks/useScope'
import { scopeOwnsOffer } from '~/modules/monetization/upgrade/scopeOffers'
import { AD_UNIT_TABS, defaultAdUnitTab } from '~/modules/monetization/inventory/adunits/adUnitTab'
import { AdUnitsTab } from '~/modules/monetization/inventory/adunits/AdUnitsTab'
import { AdUnitsTabsPage } from '~/modules/monetization/inventory/adunits/AdUnitsTabsPage'
import { ArchivedAdUnitsPage } from '~/modules/monetization/inventory/adunits/ArchivedAdUnitsPage'
import { DeploymentCenterPage } from '~/modules/monetization/inventory/deployment-center/DeploymentCenterPage'
import { AdminScopePage } from '~/modules/admin/AdminScopePage'
import { AdminDelivery } from '~/modules/admin/delivery/AdminDelivery'
import { AdminScopeFeatures } from '~/modules/admin/features/AdminScopeFeatures'
import { AdminScopeAdmPrebidServerConfig } from '~/modules/admin/features/adm/AdminScopeAdmPrebidServerConfig'
import { AdminScopeAdmConfig } from '~/modules/admin/features/adm/AdminScopeAdmConfig'
import { AdminScopePocOverridesConfig } from '~/modules/admin/features/adm/poc-overrides/AdminScopePocOverridesConfig'
import { AdminSiteConfigEdit } from '~/modules/admin/features/adm/site-config/AdminSiteConfigEdit'
import { GamManager } from '~/modules/admin/gam-manager/GamManager'
import { AdminScopeSites } from '~/modules/admin/sites/AdminScopeSites/AdminScopeSites'
import { AdminSnoozeAlarm } from '~/modules/admin/snooze-alarm/AdminSnoozeAlarm'
import { AdmAdUnitEditPage } from '~/modules/adm/inventory/adunits/AdmAdUnitEditPage'
import { AdmAdUnitImportPage } from '~/modules/adm/inventory/adunits/AdmAdUnitImportPage'
import { AdmGamIntegrationPage } from '~/modules/adm/inventory/catalog/adServers/AdmGamIntegrationPage'
import { AdmPrebidIntegrationPage } from '~/modules/adm/inventory/catalog/headerBidders/AdmPrebidIntegrationPage'
import { MarketplacePage } from '~/modules/adm/inventory/catalog/marketplace/MarketplacePage'
import { AdmTamIntegrationPage } from '~/modules/adm/inventory/catalog/wrapper/AdmTamIntegrationPage'
import { WrappersPage } from '~/modules/adm/inventory/catalog/wrapper/WrappersPage'
import { AdmContextEditPage } from '~/modules/adm/inventory/context/AdmContextEditPage'
import { AdmContextsPage } from '~/modules/adm/inventory/context/AdmContextsPage'
import { ArchivedContextsPage } from '~/modules/adm/inventory/context/ArchivedContextsPage'
import { AdmContextTabs } from '~/modules/adm/inventory/context/AdmContextTabs'
import { AdmContextKeyEditPage } from '~/modules/adm/inventory/contextKeys/AdmContextKeyEditPage'
import { AdmContextKeysPage } from '~/modules/adm/inventory/contextKeys/AdmContextKeysPage'
import { AdmAnalyticsModulePage } from '~/modules/adm/inventory/modules/analyticsModule/AdmAnalyticsModulePage'
import { AdmIdentityModulePage } from '~/modules/adm/inventory/modules/identityModule/AdmIdentityModulePage'
import { ModulesPage } from '~/modules/adm/inventory/modules/ModulesPage'
import { AdmRealTimeDataModulePage } from '~/modules/adm/inventory/modules/realTimeDataModule/AdmRealTimeDataModulePage'
import { AdmRenderingModulePage } from '~/modules/adm/inventory/modules/renderingModule/AdmRenderingModulePage'
import { SiteExtraScript } from '~/modules/adm/inventory/sites/extra-scripts/SiteExtraScript'
import { GamAndWrappers } from '~/modules/adm/inventory/sites/GamAndWrappers'
import { SiteHistory } from '~/modules/adm/inventory/sites/history/SiteHistory'
import { ModulesBySite } from '~/modules/adm/inventory/sites/ModulesBySite'
import { SiteConnectToPubstack } from '~/modules/adm/inventory/sites/SiteConnectToPubstack'
import { SiteMapping } from '~/modules/adm/inventory/sites/SiteMapping'
import { SiteOverview } from '~/modules/adm/inventory/sites/SiteOverview'
import { MediaConfigurationPage } from '~/modules/pbs/sites/site/MediaConfigurationPage'
import { SitePage } from '~/modules/adm/inventory/sites/SitePage'
import { StackEdit } from '~/modules/adm/inventory/sites/stack/StackEdit'
import { AdmLazyLoadingEditPage } from '~/modules/adm/rules/lazy-loading/AdmLazyLoadingEditPage'
import { AdmLazyLoadingPage } from '~/modules/adm/rules/lazy-loading/AdmLazyLoadingPage'
import { AdmRefreshEditPage } from '~/modules/adm/rules/refresh/AdmRefreshEditPage'
import { AdmRefreshRulesPage } from '~/modules/adm/rules/refresh/AdmRefreshRulesPage'
import { AdmRefreshGlobalSettingsPage } from '~/modules/adm/rules/refresh/AdmRefreshGlobalSettingsPage'
import { AdmRefreshPage } from '~/modules/adm/rules/refresh/AdmRefreshPage'
import { AlertsPage } from '~/modules/alerts/AlertsPage'
import { BiddersOverview } from '~/modules/analytics/bidders/BiddersOverview'
import { BidderDetailsPage } from '~/modules/analytics/bidders/details/BidderDetailsPage'
import { CoreWebVitalsOverviewPage } from '~/modules/analytics/coreWebVitals/overview/CoreWebVitalsOverviewPage'
import { ExplorePage } from '~/modules/analytics/explore/ExplorePage'
import { GamOverviewPage } from '~/modules/analytics/gamOverview/GamOverviewPage'
import { OverviewPage } from '~/modules/analytics/overview/OverviewPage'
import { RefreshPage } from '~/modules/analytics/refresh/RefreshPage'
import { ServerSideDetailPage } from '~/modules/analytics/server-side/details/ServerSideDetailPage'
import { ServerSidePage } from '~/modules/analytics/server-side/ServerSidePage'
import { SparkForPubPage } from '~/modules/analytics/spark-for-pub/SparkForPubPage'
import { SiteDetailsPage } from '~/modules/analytics/sites/details/SiteDetailsPage'
import { SitesOverviewPage } from '~/modules/analytics/sites/overview/SitesOverviewPage'
import { UserSessionPage } from '~/modules/analytics/userSession/UserSessionPage'
import { ViewabilityPage } from '~/modules/analytics/viewability/ViewabilityPage'
import { FloorPriceEditPage } from '~/modules/floor-price/FloorPriceEditPage'
import { FloorPricePage } from '~/modules/floor-price/FloorPricePage'
import { EditAdUnitsPage } from '~/modules/pbs/adunits/EditAdUnitsPage'
import { BidderRuleEditPage } from '~/modules/monetization/inventory/bidder-rules/BidderRuleEditPage'
import { BidderRulesPage } from '~/modules/monetization/inventory/bidder-rules/BidderRulesPage'
import { ArchivedBidderRulesPage } from '~/modules/monetization/inventory/rules/ArchivedBidderRulesPage'
import { ArchivedFloorPriceRulesPage } from '~/modules/monetization/inventory/rules/ArchivedFloorPriceRulesPage'
import { ArchivedLazyLoadingRulesPage } from '~/modules/monetization/inventory/rules/ArchivedLazyLoadingRulesPage'
import { ArchivedRefreshRulesPage } from '~/modules/monetization/inventory/rules/ArchivedRefreshRulesPage'
import { BidderSeatPage } from '~/modules/pbs/integrations/bidder-seats/bidder-seat/BidderSeatPage'
import { MyBidderSeatsPage } from '~/modules/pbs/integrations/bidder-seats/MyBidderSeatsPage'
import { CatalogPage } from '~/modules/pbs/integrations/catalog/CatalogPage'
import { ArchivedMediasPage } from '~/modules/pbs/sites/ArchivedMediasPage'
import { MediaIdSubtitle } from '~/modules/pbs/sites/MediaIdSubtitle'
import { SitesPage } from '~/modules/pbs/sites/SitesPage'
import { Activation } from '~/modules/pbs/sites/site/activation/Activation'
import { ConnectToPubstack } from '~/modules/pbs/sites/site/connect-to-pbstck/ConnectToPubstack'
import { SitePage as PbsSitePage } from '~/modules/pbs/sites/site/SitePage'
import { ReportsPage } from '~/modules/reports/ReportsPage'
import { AdQualityPage } from '~/modules/settings/adQuality/AdQualityPage'
import { AdQualityTabs } from '~/modules/settings/adQuality/AdQualityTabs'
import { SettingsApiKeys } from '~/modules/settings/api-keys/SettingsApiKeysPage'
import { SettingsMembersPage } from '~/modules/settings/members/SettingsMembersPage'
import { SettingsProfilePage } from '~/modules/settings/profile/SettingsProfilePage'
import { SettingsRefreshExceptionsPage } from '~/modules/settings/refreshExceptions/SettingsRefreshExceptionsPage'
import { SettingsRefreshGroupAccordionPage } from '~/modules/settings/refreshGroups/RefreshGroupAccordion/SettingsRefreshGroupAccordionPage'
import { SettingsRefreshGroupsPage } from '~/modules/settings/refreshGroups/SettingsRefreshGroupsPage'
import { SettingsHomePage } from '~/modules/settings/SettingsHomePage'
import { SettingsPage } from '~/modules/settings/SettingsPage'
import { SettingsSecurityPage } from '~/modules/settings/security/SettingsSecurityPage'
import { SettingsSitesPage } from '~/modules/settings/sites/SettingsSitesPage'
import { currentScopeState } from '~/state'
import { SETTINGS_NAV_CONFIG } from './settings'
import { AdmStacks, PbsStacks } from '~/modules/pbs/sites/site/stacks/Stacks'

/** Node traffic exists for the viewer: the scope has Node access, or staff on a scope onboarded on Node. Off otherwise, for owners and users of a staff-only scope as for a scope that never bought it. */
const subtitleAnalyticsWithNode = ({ user, hasNode }: { user?: User; hasNode?: boolean }) => {
  if (!user || !hasNode) {
    return ''
  }
  return 'Only client-side data is displayed in this page.'
}

export const Redirect: FunctionComponent<NavigateProps> = ({ to, replace, state }) => {
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  useEffect(() => {
    navigateRef.current({ pathname: to.toString(), search: searchParams.toString() }, { replace, state })
  }, [to, replace, state])

  return null
}

/**
 * The old product prefixes, and everything under them. Registered as real routes so React Router ranks
 * them above the catch-all, which would otherwise swallow a stale bookmark into the overview.
 */
export const LEGACY_PRODUCT_PATHS = ['adm', 'adm/*', 'pbs', 'pbs/*']

/** Lands on the first tab the scope can actually use, so a Node-only customer is not met by a One pitch. */
const AdUnitsIndexRedirect: FunctionComponent = () => {
  const scope = useScope()
  const { hasNode } = useNodeStaffAccess()
  return <Redirect to={defaultAdUnitTab({ hasOne: scopeOwnsOffer(scope, 'one'), hasNode })} replace />
}

/** `replace`, or Back lands on the old URL and redirects forward again. */
const LegacyProductRedirect: FunctionComponent = () => {
  const { pathname } = useLocation()
  return <Redirect to={rewriteLegacyPath(pathname) ?? '/analytics/overview'} replace />
}

/** The rule editors moved with the list (`new` and `edit/:id` kept their spelling), so the splat rides along and pre-merge editor deep links still land. */
export const UnifiedBidderRulesRedirect: FunctionComponent = () => {
  const splat = useParams()['*']
  return <Redirect to={splat ? `/monetization/inventory/bidder-rules/${splat}` : '/monetization/inventory/bidder-rules'} replace />
}

const LayoutWithAuthentication = withAuthenticationRequired(Layout, {
  loginOptions: {
    appState: {
      target: window.location.pathname + window.location.search,
    },
  },
})

type RouteConfig = Pick<RouteObject, 'path' | 'index' | 'caseSensitive'> & {
  component: ComponentType
  children?: RouteConfig[]
  metaData: PubstackRouteMeta
  errorElement?: ReactNode
}
const routeConfig: RouteConfig[] = [
  {
    path: '',
    component: () => (
      <ErrorBoundary FallbackComponent={GlobalErrorPage}>
        <LayoutWithAuthentication>
          <Outlet />
        </LayoutWithAuthentication>
      </ErrorBoundary>
    ),
    metaData: { accessRole: 'user', allowDemoRole: true },
    children: [
      {
        path: '/',
        component: () => <Redirect to={'/analytics/overview'} />,
        metaData: { accessRole: 'user', allowDemoRole: true },
      },
      {
        path: 'analytics',
        component: () => <Outlet />,
        metaData: { accessRole: 'user', allowDemoRole: true },
        children: [
          {
            path: '',
            component: () => <Redirect to={'/analytics/overview'} />,
            metaData: {
              accessRole: 'user',
              allowDemoRole: false,
              feature: 'analytics',
            },
          },
          {
            path: 'gam-overview-web',
            component: () => <GamOverviewPage inventoryType={'Web'} />,
            metaData: {
              accessRole: 'user',
              feature: 'gamOverviewWeb',
              allowDemoRole: true,
              breadcrumbLabel: 'Web overview',
            },
          },
          {
            path: 'gam-overview-amp',
            component: () => <GamOverviewPage inventoryType={'AMP'} />,
            metaData: {
              accessRole: 'user',
              feature: 'gamOverviewAmp',
              allowDemoRole: true,
              breadcrumbLabel: 'AMP overview',
            },
          },
          {
            path: 'gam-overview-app',
            component: () => <GamOverviewPage inventoryType={'App'} />,
            metaData: {
              accessRole: 'user',
              feature: 'gamOverviewApp',
              allowDemoRole: true,
              breadcrumbLabel: 'App overview',
            },
          },
          {
            path: 'server-side',
            component: () => <Outlet />,
            metaData: {
              accessRole: 'user',
              allowDemoRole: true,
              feature: 'pbsStandalone',
              breadcrumbLabel: 'Server-side',
            },
            children: [
              {
                path: '',
                component: ServerSidePage,
                metaData: {
                  accessRole: 'user',
                  allowDemoRole: true,
                  feature: 'pbsStandalone',
                },
              },
              {
                path: ':bidderName',
                component: ServerSideDetailPage,
                metaData: {
                  accessRole: 'user',
                  allowDemoRole: true,
                  feature: 'pbsStandalone',
                  breadcrumbLabel: ({ search }) => {
                    const searchParams = JSON.parse(atob(search.get('mapping') ?? "{bidder: '', bidderName: ''}"))
                    const { bidderName } = searchParams
                    return bidderName
                  },
                },
              },
            ],
          },
          {
            // `accessRole: 'pubstack'` is the real gate, matching `useIsSparkForPubUiEnabled` — a Pubstack
            // user with the scope onboarded. No `feature`: `sparkForPub` there would ask whether the scope
            // was sold the access feature, which is a different question from whether it is onboarded, and
            // onboarding a scope never stamps that feature — every onboarded scope was unreachable from the
            // menu until a customer role happened to also carry it.
            path: 'spark-for-pub',
            component: SparkForPubPage,
            metaData: {
              accessRole: 'pubstack',
              allowDemoRole: true,
              breadcrumbLabel: 'Spark for Publishers',
            },
          },
          {
            path: 'overview',
            component: OverviewPage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: true,
              breadcrumbLabel: 'Overview',
              feature: 'analytics',
            },
          },
          {
            path: 'bidders',
            component: () => <Outlet />,
            metaData: {
              accessRole: 'user',
              allowDemoRole: true,
              breadcrumbLabel: 'Bidders',
              feature: 'analytics',
              breadcrumbSubtitle: subtitleAnalyticsWithNode,
            },
            children: [
              {
                path: '',
                component: BiddersOverview,
                metaData: { accessRole: 'user', allowDemoRole: true },
              },
              {
                path: ':bidderName',
                component: BidderDetailsPage,
                metaData: {
                  accessRole: 'user',
                  allowDemoRole: true,

                  breadcrumbLabel: ({ search }) => {
                    const searchParams = JSON.parse(atob(search.get('mapping') ?? "{bidder: '', bidderName: ''}"))
                    const { bidderName } = searchParams
                    return bidderName
                  },
                  breadcrumbSubtitle: subtitleAnalyticsWithNode,
                },
              },
            ],
          },
          {
            path: 'sites',
            component: () => <Outlet />,
            metaData: {
              accessRole: 'user',
              allowDemoRole: true,
              breadcrumbLabel: 'Sites',
              feature: 'analytics',
              breadcrumbSubtitle: subtitleAnalyticsWithNode,
            },
            children: [
              {
                path: '',
                component: SitesOverviewPage,
                metaData: { accessRole: 'user', allowDemoRole: true },
              },
              {
                path: ':siteName',
                component: SiteDetailsPage,
                metaData: {
                  accessRole: 'user',
                  allowDemoRole: true,
                  breadcrumbSubtitle: subtitleAnalyticsWithNode,
                  breadcrumbLabel: ({ search }) => {
                    const searchParams = JSON.parse(atob(search.get('mapping') ?? "{site: '', siteName:''}"))
                    const { siteName } = searchParams
                    return siteName
                  },
                },
              },
            ],
          },
          {
            path: 'viewability',
            component: ViewabilityPage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: true,
              feature: 'viewability',
              breadcrumbLabel: 'Viewability',
              breadcrumbSubtitle: subtitleAnalyticsWithNode,
            },
          },
          {
            path: 'refresh',
            component: RefreshPage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: true,
              feature: ['refresh', 'admRefresh'],
              breadcrumbLabel: 'Ad refresh',
              breadcrumbSubtitle: subtitleAnalyticsWithNode,
            },
          },
          {
            path: 'user-session',
            component: UserSessionPage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: true,
              feature: 'standaloneUserSession',
              breadcrumbLabel: 'User sessions',
              breadcrumbSubtitle: ({ user, hasNode }) => {
                if (!user) {
                  return ''
                }
                return `Data displayed: Prebid${user.hasFeature('adx') ? ', AdX' : ''}${user.hasFeature('openBidding') ? ', OB' : ''}. ${subtitleAnalyticsWithNode({ user, hasNode })}`
              },
            },
          },
          {
            path: 'core-web-vitals',
            component: CoreWebVitalsOverviewPage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: true,
              feature: 'coreWebVitals',
              breadcrumbLabel: 'Core Web Vitals',
              breadcrumbSubtitle: ({ user, hasNode }: { user?: User; hasNode?: boolean }) => {
                return (
                  <>
                    <div>Key metrics impacting user experience. The following are calculated using 75th percentile, meaning 75% of users performed better than the displayed value.</div>
                    <div>{subtitleAnalyticsWithNode({ user, hasNode })}</div>
                  </>
                )
              },
            },
          },
          {
            path: 'explore',
            component: ExplorePage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: true,
              breadcrumbLabel: 'Explore',
              feature: 'analytics',
              breadcrumbSubtitle: subtitleAnalyticsWithNode,
            },
          },
        ],
      },
      {
        path: '/reports',
        component: ReportsPage,
        metaData: {
          accessRole: 'user',
          allowDemoRole: true,
          breadcrumbLabel: 'Reports',
          feature: 'analytics',
        },
      },
      {
        path: '/monitors/summary',
        component: AlertsPage,
        metaData: {
          accessRole: 'user',
          allowDemoRole: true,
          breadcrumbLabel: 'Alerts',
          feature: 'analytics',
        },
      },
      {
        path: '/admin/scopes',
        component: () => <Outlet />,
        metaData: {
          accessRole: 'pubstack',
          allowDemoRole: false,
          breadcrumbLabel: 'Admin',
        },
        children: [
          {
            path: '',
            component: AdminScopePage,
            metaData: {
              accessRole: 'pubstack',
              allowDemoRole: false,
              breadcrumbSubtitle: 'Manage all scopes',
            },
          },
          {
            path: '/admin/scopes/:scopeId',
            component: AdminScopeDetail,
            metaData: {
              accessRole: 'pubstack',
              allowDemoRole: false,
              breadcrumbLabel: ({ additionalParams }) => additionalParams && additionalParams.name,
            } as PubstackRouteMeta<AdminScope>,
            children: [
              {
                path: '',
                component: () => <Redirect to={'sites'} />,
                metaData: { accessRole: 'pubstack', allowDemoRole: false },
              },
              {
                path: 'sites',
                component: AdminScopeSites,
                metaData: { accessRole: 'pubstack', allowDemoRole: false },
              },
              {
                path: 'features',
                component: AdminScopeFeatures,
                metaData: { accessRole: 'pubstack', allowDemoRole: false },
              },
              {
                path: 'adm-settings',
                component: AdminScopeAdmConfig,
                metaData: { accessRole: 'pubstack', allowDemoRole: false },
              },
              {
                path: 'adm-prebid-server',
                component: AdminScopeAdmPrebidServerConfig,
                metaData: { accessRole: 'pubstack', allowDemoRole: false },
              },
              {
                path: 'poc-overrides',
                component: AdminScopePocOverridesConfig,
                metaData: { accessRole: 'pubstack', allowDemoRole: false },
              },
              {
                path: 'site-config',
                component: AdminSiteConfigEdit,
                metaData: { accessRole: 'pubstack', allowDemoRole: false },
              },
              {
                path: 'gam-manager',
                component: GamManager,
                metaData: { accessRole: 'pubstack', allowDemoRole: false },
              },
            ],
          },
        ],
      },
      {
        path: '/admin/delivery',
        component: AdminDelivery,
        metaData: { accessRole: 'pubstack', allowDemoRole: false },
      },
      {
        path: '/admin/snooze-alarm',
        component: AdminSnoozeAlarm,
        metaData: { accessRole: 'pubstack', allowDemoRole: false },
      },

      {
        path: '/settings',
        component: SettingsPage,
        metaData: {
          accessRole: 'user',
          allowDemoRole: false,
          breadcrumbLabel: 'Settings',
        },
        children: [
          {
            path: '',
            component: SettingsHomePage,
            metaData: { accessRole: 'user', allowDemoRole: false },
          },
          {
            path: 'members',
            component: SettingsMembersPage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: false,
              breadcrumbLabel: SETTINGS_NAV_CONFIG.members.title,
            },
          },
          {
            path: 'sites',
            component: SettingsSitesPage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: false,
              breadcrumbLabel: SETTINGS_NAV_CONFIG.sites.title,
            },
          },
          {
            path: 'profile',
            component: SettingsProfilePage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: false,
              breadcrumbLabel: SETTINGS_NAV_CONFIG.profile.title,
            },
          },
          {
            path: 'security',
            component: SettingsSecurityPage,
            metaData: {
              accessRole: 'owner',
              allowDemoRole: false,
              breadcrumbLabel: SETTINGS_NAV_CONFIG.security.title,
            },
          },
          {
            path: 'api-keys',
            component: SettingsApiKeys,
            metaData: {
              accessRole: 'user',
              allowDemoRole: false,
              breadcrumbLabel: SETTINGS_NAV_CONFIG.apiKeys.title,
            },
          },
          {
            path: 'refresh-groups',
            component: () => <Outlet />,
            metaData: {
              accessRole: 'user',
              allowDemoRole: false,
              breadcrumbLabel: SETTINGS_NAV_CONFIG.refreshGroups.title,
            },
            children: [
              {
                path: '',
                component: SettingsRefreshGroupsPage,
                metaData: { accessRole: 'user', allowDemoRole: false },
              },
              {
                path: 'new',
                component: SettingsRefreshGroupAccordionPage,
                metaData: {
                  accessRole: 'user',
                  allowDemoRole: false,
                  breadcrumbLabel: 'New refresh group',
                },
              },
              {
                path: 'edit/:id',
                component: SettingsRefreshGroupAccordionPage,
                metaData: {
                  accessRole: 'user',
                  allowDemoRole: false,

                  breadcrumbLabel: ({ additionalParams }: { additionalParams?: RefreshGroup }) => additionalParams?.name ?? 'Edit refresh group',
                },
              },
            ],
          },
          {
            path: 'refresh-exceptions',
            component: SettingsRefreshExceptionsPage,
            metaData: {
              accessRole: 'user',
              allowDemoRole: false,
              breadcrumbLabel: 'Refresh exceptions',
              breadcrumbSubtitle: 'Manage exceptions that will be applied on all refresh groups',
            },
          },
          {
            path: 'ad-quality',
            component: AdQualityTabs,
            metaData: {
              accessRole: 'owner',
              allowDemoRole: false,
              breadcrumbLabel: SETTINGS_NAV_CONFIG.adQuality.title,
            },
            children: [
              {
                path: '',
                component: () => <Redirect to={'blocklist'} replace />,
                metaData: {
                  accessRole: 'owner',
                  allowDemoRole: false,
                },
              },
              {
                path: 'blocklist',
                component: AdQualityPage,
                metaData: {
                  accessRole: 'owner',
                  allowDemoRole: false,
                },
              },
            ],
          },
        ],
      },
      {
        // Product-neutral inventory: pages that show One's and Node's data side by side, so they
        // cannot sit under either product. No `feature` on the way down, or the entries would vanish
        // from the sidebar for exactly the scopes the upgrade pitch is meant for.
        path: 'monetization/inventory/adunits',
        component: AdUnitsTabsPage,
        metaData: {
          accessRole: 'user',
          allowDemoRole: false,
          breadcrumbLabel: 'Ad units',
          breadcrumbSubtitle: 'Manage the ad units of your inventory',
        },
        children: [
          {
            path: '',
            component: AdUnitsIndexRedirect,
            metaData: { accessRole: 'user', allowDemoRole: false },
          },
          ...AD_UNIT_TABS.map((tab) => ({
            path: tab.path,
            component: () => <AdUnitsTab tab={tab} />,
            // Entitlement is decided inside the tab, which is why no feature is declared: a declared
            // one short-circuits to Forbidden and the pitch never renders.
            metaData: { accessRole: 'user' as const, allowDemoRole: false },
          })),
        ],
      },
      // Owner-only, and deliberately a page per tab rather than a filter on the lists above: archiving is
      // meant to make an ad unit go away, so the everyday list never offers a switch that could bring the
      // archived ones back into view by accident. Siblings of the tabs route rather than children of it,
      // so a tab click from an archived page cannot land on a nested URL that no longer exists.
      ...AD_UNIT_TABS.map((tab) => ({
        path: `monetization/inventory/adunits/${tab.path}/archived`,
        component: () => <ArchivedAdUnitsPage tab={tab} />,
        metaData: {
          accessRole: 'owner' as const,
          allowDemoRole: false,
          breadcrumbLabel: 'Archived ad units',
          breadcrumbSubtitle: `The ${tab.label} ad units you took out of your inventory`,
        },
      })),
      {
        // One list of floor rules, because there has only ever been one: both product pages read the
        // same `/app/floor-rules`, so the two routes were showing the same rows behind two URLs.
        //
        // `feature` lists both offers and `canAccessRoute` matches on *any* of them. No `upsell`: a rule
        // page is a tool for a stack that exists, so a scope on neither product loses the entry rather
        // than being sold from it. The cross-sell lives on Ad units, Media & stacks and the Catalog.
        path: 'monetization/inventory/floor-price',
        component: Outlet,
        metaData: {
          accessRole: 'user',
          feature: ['adstack', 'pbsStandalone'],
          allowDemoRole: false,
          breadcrumbLabel: 'Floor price rules',
          breadcrumbSubtitle: 'Create and manage rules that will be used in stacks',
        },
        children: [
          {
            path: '',
            component: FloorPricePage,
            metaData: { accessRole: 'user', feature: ['adstack', 'pbsStandalone'], allowDemoRole: false },
          },
          {
            path: 'new',
            component: FloorPriceEditPage,
            metaData: { accessRole: 'user', feature: ['adstack', 'pbsStandalone'], allowDemoRole: false, breadcrumbLabel: 'New rule' },
          },
          {
            path: 'edit/:floorRuleId',
            component: FloorPriceEditPage,
            metaData: { accessRole: 'user', feature: ['adstack', 'pbsStandalone'], allowDemoRole: false, breadcrumbLabel: 'Edit rule' },
          },
          {
            // Owner-only, matching the API, which refuses `?archived=true` to anyone below owner.
            path: 'archived',
            component: ArchivedFloorPriceRulesPage,
            metaData: { accessRole: 'owner', feature: ['adstack', 'pbsStandalone'], allowDemoRole: false, breadcrumbLabel: 'Archived rules', breadcrumbSubtitle: undefined },
          },
        ],
      },
      {
        // One rule for both products now that its editor has a section per integration shape
        // (client-side and the server-side ones). `feature` lists both offers and `canAccessRoute`
        // matches on *any* of them: a scope on either product has bidders to rule on, and the sections
        // it did not buy render blocked rather than the page being refused. No `upsell`, for the reason
        // floor price carries none: a scope with no stack has no bidder to rule on.
        path: 'monetization/inventory/bidder-rules',
        component: Outlet,
        metaData: {
          accessRole: 'user',
          feature: ['adstack', 'pbsStandalone'],
          allowDemoRole: false,
          breadcrumbLabel: 'Bidder rules',
          breadcrumbSubtitle: 'Create and manage rules that will be used in stacks',
        },
        children: [
          {
            path: '',
            component: BidderRulesPage,
            metaData: { accessRole: 'user', feature: ['adstack', 'pbsStandalone'], allowDemoRole: false },
          },
          {
            path: 'new',
            component: BidderRuleEditPage,
            metaData: { accessRole: 'user', feature: ['adstack', 'pbsStandalone'], allowDemoRole: false, breadcrumbLabel: 'New rule' },
          },
          {
            path: 'edit/:bidderRuleId',
            component: BidderRuleEditPage,
            metaData: { accessRole: 'user', feature: ['adstack', 'pbsStandalone'], allowDemoRole: false, breadcrumbLabel: 'Edit rule' },
          },
          {
            // Owner-only, matching the API, which refuses `?archived=true` to anyone below owner.
            path: 'archived',
            component: ArchivedBidderRulesPage,
            metaData: { accessRole: 'owner', feature: ['adstack', 'pbsStandalone'], allowDemoRole: false, breadcrumbLabel: 'Archived rules', breadcrumbSubtitle: undefined },
          },
        ],
      },
      {
        // The bidder catalog was already one component on two routes: what a bidder can be integrated
        // as is a scope-level question. No `feature` — a scope on neither product gets the same
        // subscribe page the Ad units tabs show, which is the entire point of letting it through.
        path: 'monetization/inventory/catalog',
        component: CatalogPage,
        metaData: { accessRole: 'user', allowDemoRole: false, breadcrumbLabel: 'Catalog', breadcrumbSubtitle: 'Connect and create seats for your inventory' },
      },
      {
        // Both products' seats on one list. The seat *page* is still per-product — it reads its ad
        // units and mappings from that product's API — so rows route by the seat's own product.
        //
        // Gated, not pitched. A scope on neither product has no seat and no way to make one; the Catalog
        // is where it is told what a seat would be for, and that entry stays on screen for everyone.
        path: 'monetization/inventory/my-bidder-seats',
        component: MyBidderSeatsPage,
        metaData: {
          accessRole: 'user',
          feature: ['adstack', 'pbsStandalone'],
          allowDemoRole: false,
          breadcrumbLabel: 'My bidder seats',
          breadcrumbSubtitle: 'Manage your seats',
        },
      },
      {
        // One work queue for both products' stacks, replacing the two identically-named pages that
        // differed only by their URL prefix. `feature` lists both offers and `canAccessRoute` matches on
        // any of them. No `upsell`: a scope with no stack has nothing to deploy, and a work queue with no
        // work in it sells nothing.
        //
        // `owner` is the stricter of the two roles it merges, and is a deliberate narrowing: Node's page
        // was open to `user`. Deploying changes what every visitor of a media is served, and One has
        // always treated that as an owner's decision.
        path: 'monetization/inventory/deployment-center',
        component: DeploymentCenterPage,
        metaData: {
          accessRole: 'owner',
          feature: ['adstack', 'pbsStandalone'],
          allowDemoRole: false,
          breadcrumbLabel: 'Deployment center',
          breadcrumbSubtitle: 'Update and deploy your stacks from one place',
        },
      },
      {
        // Every media, whatever it runs on. No `feature`, so an Analytics-only scope — which owns
        // neither product and would be refused by both product trees — can still reach its inventory.
        path: 'monetization/inventory/medias',
        component: SitesPage,
        metaData: {
          accessRole: 'user',
          allowDemoRole: false,
          breadcrumbLabel: 'Media & stacks',
          breadcrumbSubtitle: 'Manage the medias of your inventory',
        },
      },
      {
        // Owner-only, and deliberately a page rather than a filter on the list above: archiving is meant
        // to make a media go away, so the everyday list never offers a switch that could bring the
        // archived ones back into view by accident. The API gates `archived=true` the same way.
        path: 'monetization/inventory/medias/archived',
        component: ArchivedMediasPage,
        metaData: {
          accessRole: 'owner',
          allowDemoRole: false,
          breadcrumbLabel: 'Archived medias',
          breadcrumbSubtitle: 'The medias you took out of your inventory',
        },
      },
      {
        // One's pages, under Ad Management. The segment is the product's customer-facing
        // name; `adm`, which it used to be named after, is still the API namespace everywhere else.
        path: 'monetization/one',
        component: () => <Outlet />,
        metaData: {
          accessRole: 'user',
          feature: 'adstack',
          allowDemoRole: false,
          upsell: 'one',
        },
        children: [
          {
            path: 'adunits',
            component: Outlet,
            metaData: {
              accessRole: 'user',
              feature: 'adstack',
              allowDemoRole: false,
              breadcrumbLabel: 'Ad units',
              breadcrumbSubtitle: 'Manage the ad units of your stack',
            },
            children: [
              {
                // The list lives on the shared Ad units page now. The editor and the CSV import stay
                // here, so this branch keeps existing purely as their parent.
                path: '',
                component: () => <Redirect to={'/monetization/inventory/adunits/web'} replace />,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                },
              },
              {
                // Owner-only, matching the API, which refuses the ad unit PUT to anyone below owner. The
                // list disables the pen that leads here; this is what a typed URL runs into.
                path: 'edit/:adUnitId',
                component: AdmAdUnitEditPage,
                metaData: {
                  accessRole: 'owner',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: ({ additionalParams }: { additionalParams?: AdUnit }) => (additionalParams?.name ? `Edit ${additionalParams.name}` : 'Edit ad unit'),
                  breadcrumbSubtitle: undefined,
                },
              },
              {
                // `POST /adm/adunits` takes the owner gate, so the form does too — a `user` who reached it
                // from a bookmark or a shared link would otherwise fill it in and lose the work at save.
                path: 'new',
                component: AdmAdUnitEditPage,
                metaData: {
                  accessRole: 'owner',
                  allowDemoRole: false,
                  breadcrumbLabel: 'New ad unit',
                  breadcrumbSubtitle: undefined,
                },
              },
              {
                // Importing is creating in bulk, and `POST /adm/adunits/files` already takes the owner
                // gate. Leaving the route on `pubstack` meant an owner who opened it got Forbidden on a
                // page the API would have served them.
                path: 'import',
                component: AdmAdUnitImportPage,
                metaData: {
                  accessRole: 'owner',
                  allowDemoRole: false,
                  breadcrumbLabel: 'Import ad units',
                  breadcrumbSubtitle: undefined,
                },
              },
            ],
          },
          {
            path: 'context',
            component: Outlet,
            metaData: {
              accessRole: 'user',
              feature: 'adstack',
              allowDemoRole: false,
              breadcrumbLabel: 'Contexts',
              breadcrumbSubtitle: 'Push the right ad environment to the right user on the right page',
            },
            children: [
              {
                path: '',
                component: AdmContextTabs,
                metaData: { accessRole: 'user', allowDemoRole: false },
                children: [
                  {
                    path: '',
                    component: () => <Redirect to={'contexts'} />,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                    },
                  },
                  {
                    path: 'contexts',
                    component: AdmContextsPage,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                    },
                  },
                  {
                    path: 'context-keys',
                    component: AdmContextKeysPage,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                    },
                  },
                ],
              },
              // Owner-only, and deliberately a page rather than a group at the bottom of the list: archiving
              // is meant to make a context go away, so the everyday list never carries the archived ones a
              // scroll below the live ones. A sibling of the tabs route, so a tab click from here cannot
              // land on a nested URL that no longer exists.
              {
                path: 'contexts/archived',
                component: ArchivedContextsPage,
                metaData: {
                  accessRole: 'owner',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'Archived contexts',
                  breadcrumbSubtitle: 'The contexts you took out of your inventory',
                },
              },
              {
                path: 'contexts/edit/:contextId',
                component: AdmContextEditPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'Edit context',
                  breadcrumbSubtitle: undefined,
                },
              },
              {
                path: 'contexts/new',
                component: AdmContextEditPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'New context',
                  breadcrumbSubtitle: undefined,
                },
              },
              {
                path: 'context-keys/edit/:contextKeyName',
                component: AdmContextKeyEditPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'Edit context key',
                  breadcrumbSubtitle: undefined,
                },
              },
              {
                path: 'context-keys/new',
                component: AdmContextKeyEditPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'New context key',
                  breadcrumbSubtitle: undefined,
                },
              },
            ],
          },
          {
            path: 'lazy-loading',
            component: Outlet,
            metaData: {
              accessRole: 'user',
              feature: 'adstack',
              allowDemoRole: false,
              breadcrumbLabel: 'Lazy loading rules',
              breadcrumbSubtitle: 'Create and manage rules that will be used in stacks',
            },
            children: [
              {
                path: '',
                component: AdmLazyLoadingPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                },
              },
              {
                path: 'new',
                component: AdmLazyLoadingEditPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'New rule',
                },
              },
              {
                path: 'edit/:lazyLoadingRuleId',
                component: AdmLazyLoadingEditPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'Edit rule',
                },
              },
              {
                // Owner-only, matching the API, which refuses `?archived=true` to anyone below owner.
                path: 'archived',
                component: ArchivedLazyLoadingRulesPage,
                metaData: {
                  accessRole: 'owner',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'Archived rules',
                  breadcrumbSubtitle: undefined,
                },
              },
            ],
          },
          {
            path: 'refresh',
            component: Outlet,
            metaData: {
              accessRole: 'user',
              feature: 'adstack',
              allowDemoRole: false,
              breadcrumbLabel: 'Refresh rules',
              // The one rule route without a subtitle. It carried the same sentence as a line of body
              // text above its table instead, which put it below the tabs rather than under the title.
              breadcrumbSubtitle: 'Create and manage rules that will be used in stacks',
            },
            children: [
              {
                path: '',
                component: AdmRefreshPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                },
                children: [
                  {
                    path: '',
                    component: () => <Redirect to={'rules'} />,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                    },
                  },
                  {
                    path: 'rules',
                    component: AdmRefreshRulesPage,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                    },
                  },
                  {
                    path: 'global-settings',
                    component: AdmRefreshGlobalSettingsPage,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                    },
                  },
                ],
              },
              {
                path: 'new',
                component: AdmRefreshEditPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'New rule',
                },
              },
              {
                path: 'edit/:refreshRuleId',
                component: AdmRefreshEditPage,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'Edit rule',
                },
              },
              {
                // A sibling of the tabbed shell rather than a child of it: the archived list is not a
                // third tab beside Rules and Global settings, and rendering it inside would put two of
                // them above a page neither describes.
                //
                // Owner-only, matching the API, which refuses `?archived=true` to anyone below owner.
                path: 'archived',
                component: ArchivedRefreshRulesPage,
                metaData: {
                  accessRole: 'owner',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'Archived rules',
                  breadcrumbSubtitle: undefined,
                },
              },
            ],
          },
          {
            // The two products' rule pages became one at /monetization/inventory/bidder-rules. Kept as
            // a redirect so bookmarks and in-app links written before the merge still land.
            path: 'header-bidding/*',
            component: UnifiedBidderRulesRedirect,
            metaData: { accessRole: 'user', feature: 'adstack', allowDemoRole: false },
          },
          {
            path: 'sites',
            component: Outlet,
            metaData: {
              accessRole: 'user',
              feature: 'adstack',
              allowDemoRole: false,
              breadcrumbLabel: 'Media & stacks',
              breadcrumbSubtitle: 'Manage the ad management of your sites',
            },
            children: [
              // The media list is `/monetization/inventory/medias` and nothing else, so this segment is a
              // parent and not a page. It still answers on its own, because the old per-product list lived
              // here and its links are still in bookmarks and in the wild: the segment sends them on rather
              // than falling through to the catch-all, which answered a stale link with a 404.
              {
                path: '',
                component: () => <Redirect to={'/monetization/inventory/medias'} replace />,
                metaData: { accessRole: 'user', feature: 'adstack', allowDemoRole: false },
              },
              {
                path: ':siteId',
                component: Outlet,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                  breadcrumbLabel: ({ additionalParams }: { additionalParams?: Site }) => `${additionalParams?.name}`,
                  breadcrumbSubtitle: ({ additionalParams }: { additionalParams?: Site }) => (additionalParams?.id ? <MediaIdSubtitle id={additionalParams.id} /> : ''),
                },
                children: [
                  {
                    path: '',
                    component: SitePage,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                    },
                    children: [
                      {
                        path: '',
                        component: () => <Redirect to={'activation'} />,
                        metaData: { accessRole: 'user', allowDemoRole: false },
                      },
                      {
                        // The media's own settings — name, Prebid variable, SPA — reached from the pen on
                        // the media list. The flyout over the list still holds the first two.
                        path: 'configuration',
                        component: MediaConfigurationPage,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'activation',
                        component: SiteOverview,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'stacks',
                        component: AdmStacks,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'mapping',
                        component: SiteMapping,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'gamwrappers',
                        component: GamAndWrappers,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'modulesBySite',
                        component: ModulesBySite,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'connect-to-pubstack',
                        component: SiteConnectToPubstack,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'extra-scripts',
                        component: SiteExtraScript,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'history',
                        component: SiteHistory,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                    ],
                  },
                  {
                    path: 'stacks/new',
                    component: StackEdit,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                      breadcrumbLabel: 'New stack',
                    },
                  },
                  {
                    path: 'stacks/:stackId',
                    component: StackEdit,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                      breadcrumbLabel: 'Edit stack',
                    },
                  },
                  {
                    path: 'adserver/gam',
                    component: AdmGamIntegrationPage,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                      breadcrumbLabel: 'Google Ad Manager (GAM)',
                    },
                  },
                ],
              },
            ],
          },
          {
            path: 'integrations',
            component: Outlet,
            metaData: {
              accessRole: 'user',
              feature: 'adstack',
              allowDemoRole: false,
            },
            children: [
              {
                path: '',
                component: Outlet,
                metaData: {
                  accessRole: 'user',
                  feature: 'adstack',
                  allowDemoRole: false,
                },
                children: [
                  {
                    path: '',
                    component: () => <Redirect to={'/monetization/inventory/catalog'} replace />,
                    metaData: { accessRole: 'user', allowDemoRole: false },
                  },
                  {
                    // The catalog and the seat list are shared now. This branch survives only as the
                    // parent of the per-product seat page below.
                    path: 'my-bidder-seats',
                    component: Outlet,
                    metaData: { accessRole: 'user', allowDemoRole: false, breadcrumbLabel: 'My bidder seats' },
                    children: [
                      {
                        path: '',
                        component: () => <Redirect to={'/monetization/inventory/my-bidder-seats'} replace />,
                        metaData: { accessRole: 'user', allowDemoRole: false },
                      },
                      {
                        path: ':bidderSeatId',
                        component: BidderSeatPage,
                        metaData: {
                          accessRole: 'user',
                          allowDemoRole: false,
                          breadcrumbLabel: ({ additionalParams }: { additionalParams?: { bidderSeatName: string } }) => additionalParams?.bidderSeatName ?? '',
                        },
                      },
                    ],
                  },
                  {
                    path: 'modules',
                    component: Outlet,
                    metaData: {
                      accessRole: 'user',
                      allowDemoRole: false,
                      breadcrumbLabel: 'Modules',
                    },
                    children: [
                      {
                        path: '',
                        component: ModulesPage,
                        metaData: {
                          accessRole: 'user',
                          allowDemoRole: false,
                          breadcrumbSubtitle: 'Manage the modules connected to your inventory',
                        },
                      },
                      {
                        path: 'identity/:moduleId',
                        component: AdmIdentityModulePage,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                          breadcrumbLabel: ({ additionalParams }: { additionalParams?: { integrationName: string } }) => additionalParams?.integrationName ?? 'Integration',
                        },
                      },
                      {
                        path: 'real-time-data/:moduleId',
                        component: AdmRealTimeDataModulePage,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                          breadcrumbLabel: ({ additionalParams }: { additionalParams?: { integrationName: string } }) => additionalParams?.integrationName ?? 'Integration',
                        },
                      },
                      {
                        path: 'analytics/:moduleId',
                        component: AdmAnalyticsModulePage,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                          // TODO 2024-09-16 SLE AFI: It is a breadcrumb label pattern ???
                          breadcrumbLabel: ({ additionalParams }: { additionalParams?: { integrationName: string } }) => additionalParams?.integrationName ?? 'Integration',
                        },
                      },
                      {
                        path: 'rendering/:moduleId',
                        component: AdmRenderingModulePage,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                          breadcrumbLabel: ({ additionalParams }: { additionalParams?: { integrationName: string } }) => additionalParams?.integrationName ?? 'Integration',
                        },
                      },
                    ],
                  },
                  {
                    path: '',
                    component: WrappersPage,
                    metaData: {
                      accessRole: 'user',
                      feature: 'adstack',
                      allowDemoRole: false,
                      breadcrumbLabel: 'Wrappers & Ad server',
                      breadcrumbSubtitle: 'Manage the wrappers and ad server connected to your inventory',
                    },
                    children: [
                      {
                        path: '',
                        component: () => <Redirect to={'prebid'} />,
                        metaData: { accessRole: 'user', allowDemoRole: false },
                      },
                      {
                        path: 'prebid',
                        component: AdmPrebidIntegrationPage,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'tam',
                        component: AdmTamIntegrationPage,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'gam',
                        component: AdmGamIntegrationPage,
                        metaData: {
                          accessRole: 'user',
                          feature: 'adstack',
                          allowDemoRole: false,
                        },
                      },
                    ],
                  },
                  {
                    path: 'marketplace',
                    component: MarketplacePage,
                    metaData: {
                      accessRole: 'user',
                      feature: 'marketplace',
                      allowDemoRole: false,
                      breadcrumbLabel: 'Marketplace',
                      breadcrumbSubtitle: 'Manage the marketplace activation of each bidder',
                    },
                  },
                ],
              },
            ],
          },
          {
            // The page moved to the product-neutral path. Kept as a redirect rather than deleted: the
            // URL is in bookmarks, Intercom macros and every toast that ever linked to it.
            path: 'deployment-center',
            component: () => <Redirect to={'/monetization/inventory/deployment-center'} replace />,
            metaData: { accessRole: 'user', feature: 'adstack', allowDemoRole: false },
          },
        ],
      },
      {
        path: 'monetization/node',
        component: Outlet,
        metaData: {
          accessRole: 'user',
          feature: 'pbsStandalone',
          allowDemoRole: false,
          upsell: 'node',
        },
        children: [
          {
            path: 'adunits',
            component: Outlet,
            metaData: {
              accessRole: 'user',
              allowDemoRole: false,
              feature: 'pbsStandalone',
              breadcrumbLabel: 'Ad units',
              breadcrumbSubtitle: 'Manage the ad units of your stack',
            },
            children: [
              {
                // Same as One: the three Node lists are tabs of the shared page now, and this branch
                // survives only as the parent of the editor and the CSV import.
                path: '',
                component: () => <Redirect to={'/monetization/inventory/adunits/server-side'} replace />,
                metaData: {
                  accessRole: 'user',
                  allowDemoRole: false,
                  feature: 'pbsStandalone',
                },
              },
              {
                // Owner-only, matching the API. Nothing in the app links here — no node env offers a
                // settings form — so the route is the only way in, and the only place to gate it.
                path: 'edit/:adUnitId',
                component: () => <EditAdUnitsPage isEditing={true} />,
                metaData: {
                  accessRole: 'owner',
                  feature: 'pbsStandalone',
                  allowDemoRole: false,
                  breadcrumbLabel: ({ additionalParams }: { additionalParams?: PbsAdUnit }) => (additionalParams?.name ? `Edit ${additionalParams.name}` : 'Edit ad unit'),
                  breadcrumbSubtitle: undefined,
                },
              },
              {
                // `POST /pbs/adunits` takes the owner gate, so the form does too — same rule as One's `new`.
                path: 'new/:env',
                component: () => <EditAdUnitsPage isEditing={false} />,
                metaData: {
                  accessRole: 'owner',
                  allowDemoRole: false,
                  breadcrumbLabel: 'New ad unit',
                  breadcrumbSubtitle: undefined,
                },
              },
            ],
          },
          {
            path: 'integrations',
            component: Outlet,
            metaData: {
              accessRole: 'user',
              allowDemoRole: false,
            },
            children: [
              {
                path: '',
                component: () => <Redirect to={'/monetization/inventory/catalog'} replace />,
                metaData: { accessRole: 'user', allowDemoRole: false },
              },
              {
                // The catalog and the seat list are shared now. This branch survives only as the
                // parent of the per-product seat page below, and answers to the same gate as the list:
                // a scope on neither product has no seat for this URL to open.
                path: 'my-bidder-seats',
                component: Outlet,
                metaData: { accessRole: 'user', feature: ['adstack', 'pbsStandalone'], allowDemoRole: false, breadcrumbLabel: 'My bidder seats' },
                children: [
                  {
                    path: '',
                    component: () => <Redirect to={'/monetization/inventory/my-bidder-seats'} replace />,
                    metaData: { accessRole: 'user', allowDemoRole: false },
                  },
                  {
                    path: ':bidderSeatId',
                    component: BidderSeatPage,
                    metaData: {
                      accessRole: 'user',
                      allowDemoRole: false,
                      breadcrumbLabel: ({ additionalParams }: { additionalParams?: { bidderSeatName: string } }) => additionalParams?.bidderSeatName ?? '',
                    },
                  },
                ],
              },
              {
                // The Spark page's contents live on My bidder seats now, behind two staff-only tabs. The
                // route stays so the bookmarks staff typed by hand still land somewhere useful.
                path: 'spark',
                component: () => <Redirect to={'/monetization/inventory/my-bidder-seats'} replace />,
                metaData: {
                  accessRole: 'pubstack',
                  allowDemoRole: false,
                  breadcrumbLabel: 'Spark',
                },
              },
            ],
          },
          {
            path: 'sites',
            component: Outlet,
            metaData: {
              accessRole: 'user',
              feature: 'pbsStandalone',
              allowDemoRole: false,
              breadcrumbLabel: 'Media & stacks',
              breadcrumbSubtitle: 'Manage the monetisation of your platforms',
            },
            children: [
              // Redirects for the reason One's does: the media list is `/monetization/inventory/medias` and
              // nothing else, and this segment is where Node's own list used to be.
              {
                path: '',
                component: () => <Redirect to={'/monetization/inventory/medias'} replace />,
                metaData: { accessRole: 'user', feature: 'pbsStandalone', allowDemoRole: false },
              },
              {
                path: ':siteId',
                component: Outlet,
                metaData: {
                  accessRole: 'user',
                  feature: 'pbsStandalone',
                  allowDemoRole: false,
                  breadcrumbLabel: ({ additionalParams }: { additionalParams?: Site }) => additionalParams?.name ?? '',
                  breadcrumbSubtitle: ({ additionalParams }: { additionalParams?: Site }) => (additionalParams?.id ? <MediaIdSubtitle id={additionalParams.id} /> : ''),
                },
                children: [
                  {
                    path: '',
                    component: PbsSitePage,
                    metaData: {
                      accessRole: 'user',
                      feature: 'pbsStandalone',
                      allowDemoRole: false,
                    },
                    children: [
                      {
                        path: '',
                        component: () => <Redirect to={'activation'} />,
                        metaData: { accessRole: 'user', allowDemoRole: false },
                      },
                      {
                        // Same page as One's, minus the SPA section it has no endpoint for — see
                        // `MediaConfigurationPage`.
                        path: 'configuration',
                        component: MediaConfigurationPage,
                        metaData: {
                          accessRole: 'user',
                          feature: 'pbsStandalone',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'activation',
                        component: Activation,
                        metaData: {
                          accessRole: 'user',
                          feature: 'pbsStandalone',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'stacks',
                        component: PbsStacks,
                        metaData: {
                          accessRole: 'user',
                          feature: 'pbsStandalone',
                          allowDemoRole: false,
                        },
                      },
                      {
                        path: 'connect-to-pubstack',
                        component: ConnectToPubstack,
                        metaData: {
                          accessRole: 'user',
                          feature: 'pbsStandalone',
                          allowDemoRole: false,
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'bidder-rules/*',
            component: UnifiedBidderRulesRedirect,
            metaData: { accessRole: 'user', feature: 'pbsStandalone', allowDemoRole: false },
          },
          {
            path: 'deployment-center',
            component: () => <Redirect to={'/monetization/inventory/deployment-center'} replace />,
            metaData: { accessRole: 'user', feature: 'pbsStandalone', allowDemoRole: false },
          },
        ],
      },
      ...LEGACY_PRODUCT_PATHS.map((path) => ({
        path,
        component: LegacyProductRedirect,
        // Ungated, and demo is allowed through: a stale bookmark should reach whatever the new URL
        // decides to show — the page, an upgrade pitch, or a refusal — rather than being stopped here.
        metaData: { accessRole: 'user' as const, allowDemoRole: true },
      })),
      {
        path: '*',
        component: () => <Redirect to={'/analytics/overview'} />,
        metaData: { accessRole: 'user', allowDemoRole: false },
      },
    ],
  },
]

const build = (routesConfig: RouteConfig[]): RouteObject[] =>
  routesConfig.map(({ path, component, metaData, children, caseSensitive, errorElement }) => ({
    path,
    caseSensitive,
    element: <ProtectedRoute component={component} metaData={metaData} />,
    children: children ? build(children) : undefined,
    errorElement,
    handle: metaData,
  }))

const canAccessRoute = (match: RouteMatch, scopeId: ScopeId, features: string[], userRole: UserRole): boolean => {
  const element = match.route.element as ReactElement
  if (element.props) {
    const metaData = (element.props as any).metaData as PubstackRouteMeta
    if ('allowDemoRole' in metaData && getScopeRole(scopeId, userRole) === 'demo' && !metaData.allowDemoRole) return false
    if ('accessRole' in metaData && !canAccess(scopeId, userRole, metaData.accessRole)) return false
    if ('feature' in metaData && metaData.feature) {
      const featureList = Array.isArray(metaData.feature) ? metaData.feature : [metaData.feature]
      // A route that upsells stays reachable without its feature: the menu entry is the cross-sell, and
      // the page behind it explains what is missing. Everything else keeps disappearing as it does today.
      if (!featureList.some((f) => hasFeature(scopeId, features, f))) return upsellOffers(metaData).length > 0
    }
    return true
  }
  return false
}

export const routes = build(routeConfig)

export const useCanAccessRoute = () => {
  const user = useAuthenticatedUser()
  const currentScope = useAtomValue(currentScopeState)

  return useCallback(
    (path: string): boolean => {
      const matches = matchRoutes(routes, path)
      // Entitlement belongs to the account, not to whoever is looking. A Pubstack user has every
      // feature synthesised for the scope they are in, so filtering the sidebar on their own features
      // shows staff a menu no customer of that scope has — and hides the one thing they are there to
      // check. Roles below stay the viewer's: what the account owns and what this person may do are
      // different questions. Falls back to the viewer's features against a backend older than
      // `accessFeatures`, which is exactly the previous behaviour.
      const baseFeatures = currentScope.accessFeatures?.map((feature) => scopeFeature(currentScope.id, feature)) ?? user.features
      // Staff reach the Node pages as soon as the scope is onboarded on Node, before user access is granted.
      const features = nodeStaffStateOfScope(currentScope, user).isStaffOnNode ? [...baseFeatures, scopeFeature(currentScope.id, 'pbsStandalone')] : baseFeatures
      return matches?.every((match) => canAccessRoute(match, currentScope.id, features, user.userRoles)) ?? false
    },
    [user, currentScope]
  )
}

/**
 * Whether a path is one of the pages `pbsStandalone` switches on: its own route lists the feature. The
 * menu asks it to mark those entries for Pubstack staff, who see them on a Node scope whatever their role.
 */
export const useIsNodeRoute = () =>
  useCallback((path: string): boolean => {
    const element = matchRoutes(routes, path)?.at(-1)?.route.element as ReactElement | undefined
    const feature = (element?.props as { metaData?: PubstackRouteMeta } | undefined)?.metaData?.feature
    return (Array.isArray(feature) ? feature : [feature]).includes('pbsStandalone')
  }, [])

const getRouteFromPathname = (pathname: string) => {
  const matches = matchRoutes(routes, pathname) || []
  return matches.length ? matches[matches.length - 1] : undefined
}

export const useCurrentRoute = () => {
  const location = useLocation()
  return getRouteFromPathname(location.pathname)
}
